export const DATABASE_CONNECTION_ERROR_EVENT = 'ff-database-connection-error';
export const API_ERROR_TOAST_EVENT = 'ff-api-error-toast';
/** AuthContext listens and clears the cached user so ProtectedRoute can send the login page. */
export const SESSION_EXPIRED_EVENT = 'ff-session-expired';
export const SESSION_EXPIRED_TOAST = 'Your session expired. Please log in again.';

type ApiErrorShape = {
  message?: string;
  code?: string;
  config?: { url?: string };
  response?: {
    status?: number;
    data?: { error?: string; detail?: string; message?: string; code?: string } | string | null;
  };
};

export function isDatabaseConnectionApiError(err: unknown): boolean {
  const e = err as ApiErrorShape;
  const data = e?.response?.data;
  if (data && typeof data === 'object') {
    const code = String(data.code || '').toLowerCase();
    const error = String(data.error || '').toLowerCase();
    if (code === 'database_connection_error') return true;
    if (error.includes('database connection')) return true;
  }
  const status = e?.response?.status;
  const msg = String(e?.message || '').toLowerCase();
  return status === 503 && msg.includes('database');
}

export function isUnauthorizedApiError(err: unknown): boolean {
  return (err as ApiErrorShape)?.response?.status === 401;
}

export function isDatabaseConnectionUserMessage(text: string | null | undefined): boolean {
  return String(text || '').toLowerCase().includes('database connection');
}

export function isUnauthorizedUserMessage(text: string | null | undefined): boolean {
  const value = String(text || '').toLowerCase();
  return (
    value.includes('status code 401') ||
    value.includes('unauthorized') ||
    value.includes('session expired')
  );
}

/** Inline page banners hide these; the filled red toast already shows them. */
export function isToastOnlyApiErrorMessage(text: string | null | undefined): boolean {
  return isDatabaseConnectionUserMessage(text) || isUnauthorizedUserMessage(text);
}

export function databaseConnectionToastMessage(err?: unknown): string {
  const e = err as ApiErrorShape;
  const data = e?.response?.data;
  if (data && typeof data === 'object') {
    const title = String(data.error || 'Database Connection Error').trim() || 'Database Connection Error';
    const message = String(data.message || '').trim();
    if (message && message.toLowerCase() !== title.toLowerCase()) {
      return `${title}. ${message}`;
    }
    return title;
  }
  return 'Database Connection Error. Unable to connect to the database. Please try again in a moment.';
}

export function unauthorizedToastMessage(_err?: unknown): string {
  return SESSION_EXPIRED_TOAST;
}

export type ApiErrorToastDetail = {
  title?: string;
  message?: string;
  requestId?: string;
  errorCode?: string;
};

export function apiErrorToastMeta(err?: unknown): Pick<ApiErrorToastDetail, 'requestId' | 'errorCode'> {
  const e = err as ApiErrorShape & {
    response?: ApiErrorShape['response'] & { headers?: Record<string, unknown> };
  };
  const data = e?.response?.data;
  const body = data && typeof data === 'object' ? data : null;
  const headers = e?.response?.headers;
  const headerId =
    headers && typeof headers === 'object'
      ? String(headers['x-request-id'] || headers['X-Request-ID'] || '').trim()
      : '';
  const bodyId = body ? String((body as { request_id?: string }).request_id || '').trim() : '';
  const errorCode = body ? String(body.code || '').trim() : '';
  return {
    requestId: headerId || bodyId || undefined,
    errorCode: errorCode || undefined,
  };
}

export function dispatchApiErrorToast(detail: ApiErrorToastDetail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(API_ERROR_TOAST_EVENT, { detail }));
}

/**
 * Produces a clear user message when the browser/axios has no useful HTTP body
 * (typical: `Network Error`, `ERR_NETWORK`, timeouts) vs. a normal API error payload.
 */
export function getHttpErrorMessageForUser(err: unknown, fallback: string): string {
  const e = err as ApiErrorShape;
  const st = e?.response?.status;
  const data = e?.response?.data;
  if (isDatabaseConnectionApiError(err)) return databaseConnectionToastMessage(err);
  if (isUnauthorizedApiError(err)) return unauthorizedToastMessage(err);
  if (data && typeof data === 'object') {
    const d = data as { error?: string; detail?: string; message?: string };
    if (d.error != null && String(d.error).trim() !== '') return String(d.error);
    if (d.detail != null && String(d.detail).trim() !== '') return String(d.detail);
    if (d.message != null && String(d.message).trim() !== '') return String(d.message);
  }
  if (data && typeof data === 'string' && data.trim()) {
    return data.trim();
  }
  if (st && st >= 400) {
    return e?.message?.trim() || fallback;
  }
  const code = e?.code;
  const msg = (e?.message || '').trim();
  if (code === 'ECONNABORTED' || msg.toLowerCase().includes('timeout')) {
    return 'The request timed out. The server may be slow to respond (e.g. cold start); wait a few seconds and try Save again.';
  }
  if (code === 'ERR_NETWORK' || msg === 'Network Error') {
    return 'Could not reach the server. Check that you are online, then try again. If the app was idle, the API may be waking up—wait 10–30 seconds and retry.';
  }
  if (msg) return msg;
  return fallback;
}

